import { Fragment, useState, type ReactNode } from "react";
import { Link } from "react-router";
import { useQuery } from "@tanstack/react-query";
import { ChevronDown, Sparkles } from "lucide-react";
import type { SearchSummary, SummaryEvidence } from "../../../shared/contracts.js";
import { api } from "../../api.js";
import { Spinner } from "../../components/ui/States.js";
import { cx } from "../../lib/cx.js";

const KIND_LABEL: Record<SummaryEvidence["kind"], string> = {
  aviso: "Aviso del índice",
  web: "Internet",
  vin: "VIN",
  registro: "Registro de transporte",
  cruce: "Cruce automático",
  google: "Google",
};

/**
 * Resumen con IA arriba de los resultados (como el de un buscador). Se pide solo al
 * abrir la búsqueda; mientras la búsqueda en internet corre, el servidor responde
 * `pending` y se vuelve a pedir. Cuando internet termina, se pide una vez más para
 * incluir esos resultados.
 */
export function AiSummary({ searchId, liveDone }: { searchId: string; liveDone: boolean }) {
  const q = useQuery({
    queryKey: ["search-summary", searchId, liveDone],
    queryFn: () => api.searches.summary(searchId),
    staleTime: Infinity,
    retry: 1,
    placeholderData: (prev) => prev,
    refetchInterval: (query) => (query.state.data?.status === "pending" ? 2500 : false),
  });
  const s = q.data;
  // Mientras se regenera tras terminar internet, se mantiene el anterior a la vista.
  if (q.isError || s?.status === "disabled") return null;

  return (
    <section aria-label="Resumen con IA" aria-busy={!s || s.status === "pending"}
      className="rounded-xl border border-border bg-surface px-4 py-3 shadow-card">
      <div className="mb-1.5 flex items-center gap-1.5 text-[12.5px] font-semibold text-accent">
        <Sparkles className="size-4" aria-hidden="true" />
        Resumen con IA
      </div>
      {!s || s.status === "pending" ? (
        <div className="space-y-2" role="status">
          <div className="flex items-center gap-2 text-[13px] text-muted">
            <Spinner /> {s?.status === "pending" ? "Esperando los resultados de internet…" : "Leyendo los resultados…"}
          </div>
          <div className="h-3 w-11/12 animate-pulse rounded bg-surface-2" />
          <div className="h-3 w-3/4 animate-pulse rounded bg-surface-2" />
        </div>
      ) : (
        <SummaryBody s={s} searchId={searchId} />
      )}
    </section>
  );
}

function SummaryBody({ s, searchId }: { s: SearchSummary; searchId: string }) {
  const [open, setOpen] = useState(false);
  const byId = new Map(s.evidence.map((e) => [e.id, e]));
  const inline = [...(s.resumen ?? "").matchAll(/\[([A-Z]\d{1,3})\]/g)].map((m) => m[1]!);
  const cited = new Set([...inline, ...s.puntos.flatMap((p) => p.citas)]);
  const sources = s.evidence.filter((e) => cited.has(e.id));

  return (
    <div className="space-y-2 text-[14px] leading-relaxed text-fg">
      {s.resumen && <p>{withCitations(s.resumen, byId)}</p>}
      {s.puntos.length > 0 && (
        <ul className="list-disc space-y-1 pl-5">
          {s.puntos.map((p, i) => (
            <li key={i}>
              {p.texto}{" "}
              {p.citas.map((c) => <Cite key={c} id={c} ev={byId.get(c)} />)}
            </li>
          ))}
        </ul>
      )}
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1 pt-1 text-[12px] text-subtle">
        {sources.length > 0 && (
          <button type="button" onClick={() => setOpen((o) => !o)} aria-expanded={open}
            className="inline-flex items-center gap-1 font-medium text-muted hover:text-fg">
            {sources.length} {sources.length === 1 ? "fuente" : "fuentes"}
            <ChevronDown className={cx("size-3.5 transition-transform", open && "rotate-180")} aria-hidden="true" />
          </button>
        )}
        <span>
          {s.status === "fallback"
            ? "Cruces automáticos (la IA no respondió)."
            : s.evidence.some((e) => e.kind === "google")
              ? "Generado con IA a partir de los resultados y de una búsqueda en Google; puede contener errores. Revise las fuentes."
              : "Generado con IA a partir de los resultados; puede contener errores. Revise las fuentes."}
        </span>
        {s.livePending && <span>· sin los resultados de internet aún</span>}
      </div>
      {s.hasSuggestions && searchId && (
        // Sugerencias de Google (obligatorias con respuestas investigadas en Google). HTML de
        // Google aislado: el servidor lo entrega con CSP sandbox y sin scripts.
        <iframe
          title="Sugerencias de búsqueda de Google"
          src={`/api/searches/${encodeURIComponent(searchId)}/summary/suggestions`}
          sandbox="allow-popups allow-popups-to-escape-sandbox"
          referrerPolicy="no-referrer"
          className="h-16 w-full rounded-md border-0"
        />
      )}
      {open && (
        <ol className="space-y-1 border-t border-border pt-2 text-[12.5px]">
          {sources.map((e) => (
            <li key={e.id} className="flex gap-2">
              <span className="w-8 shrink-0 font-mono text-subtle">{e.id}</span>
              <span className="min-w-0">
                <span className="text-subtle">{KIND_LABEL[e.kind]} · </span>
                <EvidenceLink ev={e}>{e.label}</EvidenceLink>
              </span>
            </li>
          ))}
        </ol>
      )}
    </div>
  );
}

/** "texto [A1][C2] más" → texto con chips de cita. */
function withCitations(text: string, byId: Map<string, SummaryEvidence>): ReactNode[] {
  return text.split(/(\[[A-Z]\d{1,3}\])/g).map((part, i) => {
    const m = /^\[([A-Z]\d{1,3})\]$/.exec(part);
    return m ? <Cite key={i} id={m[1]!} ev={byId.get(m[1]!)} /> : <Fragment key={i}>{part}</Fragment>;
  });
}

function Cite({ id, ev }: { id: string; ev: SummaryEvidence | undefined }) {
  const cls = "mx-0.5 inline-flex rounded bg-surface-2 px-1 align-[1px] font-mono text-[11px] font-semibold text-muted hover:text-accent";
  if (!ev) return null;
  return (
    <EvidenceLink ev={ev} className={cls} title={`${KIND_LABEL[ev.kind]}: ${ev.label}`}>
      {id}
    </EvidenceLink>
  );
}

function EvidenceLink({ ev, children, className, title }: { ev: SummaryEvidence; children: ReactNode; className?: string; title?: string }) {
  const cls = className ?? "text-fg hover:text-accent hover:underline";
  if (!ev.href) return <span className={className} title={title}>{children}</span>;
  if (ev.href.startsWith("/")) return <Link to={ev.href} className={cls} title={title}>{children}</Link>;
  return (
    <a href={ev.href} target="_blank" rel="noopener noreferrer nofollow" referrerPolicy="no-referrer" className={cls} title={title}>
      {children}
    </a>
  );
}
